import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronLeft, Plus, Trash2, Edit2, Calendar, Clock, User, Building, Phone, Mail, FileDown, X, Check, ChevronDown, MapPin, ClipboardList, Stethoscope, FileText, Star, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { exportAppointmentsToPDF, exportSingleAppointmentPDF } from '../utils/pdfExport';
import TimelineButton from '../components/TimelineButton';
import { AttachmentSection } from '../components/AttachmentSection';
import { todayLocal, formatLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppointmentTracker = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [expandedId, setExpandedId] = useState(null);
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    date: '', time: '', provider_name: '', provider_title: '', company_name: '',
    address: '', phone: '', email: '', appointment_type: '', status: 'scheduled',
    questions_for_provider: [], concerns_to_discuss: '', summary: '',
    meds_ordered: [], meds_changed: [], meds_discontinued: [],
    labs_ordered: [], labs_reviewed: '', follow_up_instructions: '',
    next_appointment: '', notes: ''
  });

  const [providerForm, setProviderForm] = useState({
    name: '', title: '', specialty: '', company_name: '', address: '',
    phone: '', email: '', fax: '', website: '', notes: '', is_favorite: false
  });
  
  const [questionInput, setQuestionInput] = useState('');
  const [medInput, setMedInput] = useState({ ordered: '', changed: '', discontinued: '' });
  const [labInput, setLabInput] = useState('');

  const appointmentTypes = ['Initial Consultation', 'Follow-up', 'Lab Review', 'Medication Check', 'Therapy Session', 'Emergency', 'Telehealth', 'Other'];
  const specialties = ['Pediatric Neurologist', 'Immunologist', 'Integrative Medicine', 'PANDAS/PANS Specialist', 'Psychiatrist', 'Psychologist', 'Occupational Therapist', 'Speech Therapist', 'Pediatrician', 'Naturopath', 'Other'];

  useEffect(() => { fetchAppointments(); fetchProviders(); }, [token]);

  const fetchAppointments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/appointments`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setAppointments(data.appointments || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchProviders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/providers`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setProviders(data.providers || []);
    } catch (e) { console.error(e); }
  };

  const selectProvider = (provider) => {
    setFormData({
      ...formData,
      provider_name: provider.name,
      provider_title: provider.title || '',
      company_name: provider.company_name || '',
      address: provider.address || '',
      phone: provider.phone || '',
      email: provider.email || ''
    });
    setShowProviderDropdown(false);
    toast.success(`Auto-filled ${provider.name}'s info`);
  };

  const handleProviderSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingProvider ? `${API_URL}/api/providers/${editingProvider.id}` : `${API_URL}/api/providers`;
      const res = await fetch(url, {
        method: editingProvider ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(providerForm)
      });
      if (res.ok) {
        toast.success(editingProvider ? 'Provider updated' : 'Provider saved');
        setShowProviderModal(false);
        resetProviderForm();
        fetchProviders();
      }
    } catch (e) { toast.error('Error saving provider'); }
  };

  const handleDeleteProvider = async (id) => {
    if (!window.confirm('Delete this provider?')) return;
    try {
      await fetch(`${API_URL}/api/providers/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Provider deleted');
      fetchProviders();
    } catch (e) { toast.error('Error'); }
  };

  const toggleProviderFavorite = async (provider) => {
    try {
      await fetch(`${API_URL}/api/providers/${provider.id}/favorite`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } });
      fetchProviders();
    } catch (e) { toast.error('Error'); }
  };

  const resetProviderForm = () => {
    setEditingProvider(null);
    setProviderForm({ name: '', title: '', specialty: '', company_name: '', address: '', phone: '', email: '', fax: '', website: '', notes: '', is_favorite: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingAppointment ? `${API_URL}/api/appointments/${editingAppointment.id}` : `${API_URL}/api/appointments`;
      const res = await fetch(url, {
        method: editingAppointment ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success(editingAppointment ? 'Updated' : 'Created');
        setShowModal(false);
        resetForm();
        fetchAppointments();
      }
    } catch (e) { toast.error('Error saving'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      await fetch(`${API_URL}/api/appointments/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Deleted');
      fetchAppointments();
    } catch (e) { toast.error('Error'); }
  };

  const handleEdit = (appt) => {
    setEditingAppointment(appt);
    setFormData({ ...appt, questions_for_provider: appt.questions_for_provider || [], meds_ordered: appt.meds_ordered || [], meds_changed: appt.meds_changed || [], meds_discontinued: appt.meds_discontinued || [], labs_ordered: appt.labs_ordered || [] });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingAppointment(null);
    setFormData({ date: '', time: '', provider_name: '', provider_title: '', company_name: '', address: '', phone: '', email: '', appointment_type: '', status: 'scheduled', questions_for_provider: [], concerns_to_discuss: '', summary: '', meds_ordered: [], meds_changed: [], meds_discontinued: [], labs_ordered: [], labs_reviewed: '', follow_up_instructions: '', next_appointment: '', notes: '' });
    setQuestionInput(''); setMedInput({ ordered: '', changed: '', discontinued: '' }); setLabInput('');
  };

  const addToList = (field, value, setter, inputKey) => {
    if (value.trim()) {
      setFormData({ ...formData, [field]: [...(formData[field] || []), value.trim()] });
      if (inputKey) setter({ ...medInput, [inputKey]: '' });
      else setter('');
    }
  };

  const removeFromList = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const today = todayLocal();
  const upcomingAppts = appointments.filter(a => a.date >= today && a.status === 'scheduled').sort((a, b) => new Date(a.date) - new Date(b.date));
  const pastAppts = appointments.filter(a => a.date < today || a.status === 'completed').sort((a, b) => new Date(b.date) - new Date(a.date));

  const getStatusColor = (status) => ({ scheduled: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' }[status] || 'bg-stone-100');

  const formatDate = (d) => formatLocalDate(d, 'en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-blue-50 pb-20">
      <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2"><ChevronLeft className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-xl font-nunito font-bold text-[#2D2D2D]">Appointment Tracker</h1>
                <p className="text-sm text-[#57534E]">Track provider visits, notes, and follow-ups</p>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-accent hover:bg-accent/90" data-testid="add-appointment-btn">
              <Plus className="w-4 h-4 mr-2" /> New Appointment
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="flex gap-3 flex-wrap">
          <TimelineButton />
          <Button variant="outline" size="sm" onClick={async () => { try { await exportAppointmentsToPDF(appointments, token); } catch (e) { toast.error('Export failed'); } }} disabled={appointments.length === 0} data-testid="export-all-btn">
            <FileDown className="w-4 h-4 mr-2" /> Export All as PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => { resetProviderForm(); setShowProviderModal(true); }} data-testid="add-provider-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add Provider
          </Button>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{upcomingAppts.length}</p>
            <p className="text-xs text-[#57534E]">Upcoming</p>
          </Card>
          <Card className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{pastAppts.filter(a => a.status === 'completed').length}</p>
            <p className="text-xs text-[#57534E]">Completed</p>
          </Card>
          <Card className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-accent">{appointments.length}</p>
            <p className="text-xs text-[#57534E]">Total Appts</p>
          </Card>
          <Card className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{providers.length}</p>
            <p className="text-xs text-[#57534E]">Providers</p>
          </Card>
        </div>

        <div className="flex gap-2 border-b border-stone-200">
          <button onClick={() => setActiveTab('upcoming')} className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'upcoming' ? 'border-accent text-accent' : 'border-transparent text-[#57534E]'}`}>Upcoming ({upcomingAppts.length})</button>
          <button onClick={() => setActiveTab('past')} className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'past' ? 'border-accent text-accent' : 'border-transparent text-[#57534E]'}`}>Past ({pastAppts.length})</button>
          <button onClick={() => setActiveTab('providers')} className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'providers' ? 'border-accent text-accent' : 'border-transparent text-[#57534E]'}`}><Users className="w-4 h-4 inline mr-1" />Providers ({providers.length})</button>
        </div>

        {activeTab === 'providers' ? (
          <div className="space-y-3">
            {providers.length === 0 ? (
              <Card className="bg-white rounded-xl p-8 text-center">
                <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No providers saved</h3>
                <p className="text-sm text-[#57534E] mb-4">Save your providers for quick auto-fill when creating appointments.</p>
                <Button onClick={() => { resetProviderForm(); setShowProviderModal(true); }} className="bg-accent hover:bg-accent/90">
                  <UserPlus className="w-4 h-4 mr-2" /> Add First Provider
                </Button>
              </Card>
            ) : (
              providers.map(provider => (
                <Card key={provider.id} className="bg-white rounded-xl border p-4" data-testid={`provider-${provider.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {provider.is_favorite && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                        {provider.specialty && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">{provider.specialty}</span>}
                      </div>
                      <h4 className="font-bold text-lg text-[#2D2D2D]">{provider.name}</h4>
                      {provider.title && <p className="text-sm text-[#57534E]">{provider.title}</p>}
                      {provider.company_name && <p className="text-sm text-[#A8A29E] flex items-center gap-1"><Building className="w-3 h-3" /> {provider.company_name}</p>}
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#57534E]">
                        {provider.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {provider.phone}</span>}
                        {provider.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {provider.email}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => toggleProviderFavorite(provider)} className="p-2" title="Toggle favorite">
                        <Star className={`w-4 h-4 ${provider.is_favorite ? 'text-yellow-500 fill-yellow-500' : 'text-stone-300'}`} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingProvider(provider); setProviderForm({...provider}); setShowProviderModal(true); }} className="p-2">
                        <Edit2 className="w-4 h-4 text-[#57534E]" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteProvider(provider.id)} className="p-2 hover:bg-red-50">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        ) : loading ? <Card className="p-8 text-center"><p>Loading...</p></Card> : (
          <div className="space-y-3">
            {(activeTab === 'upcoming' ? upcomingAppts : pastAppts).length === 0 ? (
              <Card className="bg-white rounded-xl p-8 text-center">
                <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No {activeTab} appointments</h3>
                <p className="text-sm text-[#57534E] mb-4">{activeTab === 'upcoming' ? 'Schedule your next provider visit.' : 'Your past appointments will appear here.'}</p>
              </Card>
            ) : (
              (activeTab === 'upcoming' ? upcomingAppts : pastAppts).map(appt => (
                <Card key={appt.id} className="bg-white rounded-xl border p-4" data-testid={`appt-${appt.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1" onClick={() => setExpandedId(expandedId === appt.id ? null : appt.id)} style={{cursor: 'pointer'}}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(appt.status)}`}>{appt.status}</span>
                        {appt.appointment_type && <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100">{appt.appointment_type}</span>}
                      </div>
                      <div className="flex items-center gap-2 text-accent font-semibold">
                        <Calendar className="w-4 h-4" /> {formatDate(appt.date)}
                        {appt.time && <><Clock className="w-4 h-4 ml-2" /> {appt.time}</>}
                      </div>
                      <h4 className="font-bold text-lg text-[#2D2D2D] mt-1">{appt.provider_name}</h4>
                      {appt.provider_title && <p className="text-sm text-[#57534E]">{appt.provider_title}</p>}
                      {appt.company_name && <p className="text-sm text-[#A8A29E] flex items-center gap-1"><Building className="w-3 h-3" /> {appt.company_name}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={async () => { try { await exportSingleAppointmentPDF(appt, token); } catch (e) { toast.error('Export failed'); } }} className="p-2" title="Export PDF"><FileText className="w-4 h-4 text-[#57534E]" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(appt)} className="p-2"><Edit2 className="w-4 h-4 text-[#57534E]" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(appt.id)} className="p-2 hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                  
                  {expandedId === appt.id && (
                    <div className="mt-4 pt-4 border-t border-stone-100 space-y-3 text-sm">
                      {(appt.phone || appt.email || appt.address) && (
                        <div className="bg-stone-50 p-3 rounded-lg space-y-1">
                          {appt.phone && <p className="flex items-center gap-2"><Phone className="w-3 h-3" /> {appt.phone}</p>}
                          {appt.email && <p className="flex items-center gap-2"><Mail className="w-3 h-3" /> {appt.email}</p>}
                          {appt.address && <p className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {appt.address}</p>}
                        </div>
                      )}
                      {appt.questions_for_provider?.length > 0 && (
                        <div><p className="font-semibold text-accent mb-1">Questions for Provider:</p>
                          <ul className="list-disc list-inside text-[#57534E]">{appt.questions_for_provider.map((q, i) => <li key={i}>{q}</li>)}</ul>
                        </div>
                      )}
                      {appt.concerns_to_discuss && <div><p className="font-semibold mb-1">Concerns:</p><p className="text-[#57534E]">{appt.concerns_to_discuss}</p></div>}
                      {appt.summary && <div><p className="font-semibold text-green-700 mb-1">Summary:</p><p className="text-[#57534E] bg-green-50 p-2 rounded">{appt.summary}</p></div>}
                      {appt.meds_ordered?.length > 0 && <div><p className="font-semibold text-blue-700 mb-1">Meds Ordered:</p><p className="text-[#57534E]">{appt.meds_ordered.join(', ')}</p></div>}
                      {appt.meds_changed?.length > 0 && <div><p className="font-semibold text-yellow-700 mb-1">Meds Changed:</p><p className="text-[#57534E]">{appt.meds_changed.join(', ')}</p></div>}
                      {appt.meds_discontinued?.length > 0 && <div><p className="font-semibold text-red-700 mb-1">Meds Discontinued:</p><p className="text-[#57534E]">{appt.meds_discontinued.join(', ')}</p></div>}
                      {appt.labs_ordered?.length > 0 && <div><p className="font-semibold text-purple-700 mb-1">Labs Ordered:</p><p className="text-[#57534E]">{appt.labs_ordered.join(', ')}</p></div>}
                      {appt.labs_reviewed && <div><p className="font-semibold mb-1">Labs Reviewed:</p><p className="text-[#57534E]">{appt.labs_reviewed}</p></div>}
                      {appt.follow_up_instructions && <div><p className="font-semibold mb-1">Follow-up Instructions:</p><p className="text-[#57534E]">{appt.follow_up_instructions}</p></div>}
                      {appt.notes && <div><p className="font-semibold mb-1">Notes:</p><p className="text-[#57534E]">{appt.notes}</p></div>}
                      <div className="pt-3 border-t border-stone-100">
                        <AttachmentSection
                          parentType="appointment"
                          parentId={appt.id}
                          token={token}
                          title="Photos & Documents"
                        />
                      </div>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between mb-4">
                <h2 className="text-xl font-bold">{editingAppointment ? 'Edit' : 'New'} Appointment</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}><X className="w-5 h-5" /></Button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Provider Auto-fill */}
                {providers.length > 0 && !editingAppointment && (
                  <div className="bg-purple-50 p-3 rounded-lg mb-4">
                    <label className="block text-sm font-medium text-purple-800 mb-2">
                      <Users className="w-4 h-4 inline mr-1" /> Quick Fill from Saved Providers
                    </label>
                    <div className="relative">
                      <Button type="button" variant="outline" onClick={() => setShowProviderDropdown(!showProviderDropdown)} className="w-full justify-between bg-white">
                        Select a provider to auto-fill... <ChevronDown className="w-4 h-4" />
                      </Button>
                      {showProviderDropdown && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setShowProviderDropdown(false)} />
                          <div className="absolute top-full mt-1 w-full bg-white border rounded-lg shadow-lg z-40 max-h-48 overflow-y-auto">
                            {providers.filter(p => p.is_favorite).length > 0 && (
                              <div className="px-3 py-1 bg-yellow-50 text-xs font-medium text-yellow-800">⭐ Favorites</div>
                            )}
                            {providers.filter(p => p.is_favorite).map(p => (
                              <button key={p.id} type="button" onClick={() => selectProvider(p)} className="w-full px-3 py-2 text-left hover:bg-purple-50 text-sm flex items-center gap-2">
                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                <span className="font-medium">{p.name}</span>
                                {p.specialty && <span className="text-xs text-[#A8A29E]">({p.specialty})</span>}
                              </button>
                            ))}
                            {providers.filter(p => !p.is_favorite).length > 0 && (
                              <div className="px-3 py-1 bg-stone-50 text-xs font-medium text-stone-600">All Providers</div>
                            )}
                            {providers.filter(p => !p.is_favorite).map(p => (
                              <button key={p.id} type="button" onClick={() => selectProvider(p)} className="w-full px-3 py-2 text-left hover:bg-purple-50 text-sm">
                                <span className="font-medium">{p.name}</span>
                                {p.specialty && <span className="text-xs text-[#A8A29E] ml-2">({p.specialty})</span>}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Date *</label><Input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required /></div>
                  <div><label className="block text-sm font-medium mb-1">Time</label><Input type="time" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Provider Name *</label><Input value={formData.provider_name} onChange={e => setFormData({...formData, provider_name: e.target.value})} placeholder="Dr. Smith" required /></div>
                  <div><label className="block text-sm font-medium mb-1">Title</label><Input value={formData.provider_title} onChange={e => setFormData({...formData, provider_title: e.target.value})} placeholder="Pediatric Neurologist" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Company/Practice</label><Input value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} placeholder="Children's Hospital" /></div>
                  <div><label className="block text-sm font-medium mb-1">Type</label>
                    <select value={formData.appointment_type} onChange={e => setFormData({...formData, appointment_type: e.target.value})} className="w-full h-10 px-3 border rounded-lg text-sm">
                      <option value="">Select...</option>{appointmentTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div><label className="block text-sm font-medium mb-1">Address</label><Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="123 Medical Center Dr" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Phone</label><Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" /></div>
                  <div><label className="block text-sm font-medium mb-1">Email</label><Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="office@provider.com" /></div>
                </div>
                <div><label className="block text-sm font-medium mb-1">Status</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full h-10 px-3 border rounded-lg text-sm">
                    <option value="scheduled">Scheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="border-t pt-4 mt-4"><h3 className="font-semibold text-accent mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Pre-Appointment</h3>
                  <div className="mb-3"><label className="block text-sm font-medium mb-1">Questions for Provider</label>
                    <div className="flex gap-2"><Input value={questionInput} onChange={e => setQuestionInput(e.target.value)} placeholder="Add a question..." onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addToList('questions_for_provider', questionInput, setQuestionInput))} /><Button type="button" variant="outline" onClick={() => addToList('questions_for_provider', questionInput, setQuestionInput)}>Add</Button></div>
                    {formData.questions_for_provider?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{formData.questions_for_provider.map((q, i) => <span key={i} className="text-xs px-2 py-1 bg-accent/10 text-accent rounded-full flex items-center gap-1">{q} <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromList('questions_for_provider', i)} /></span>)}</div>}
                  </div>
                  <div><label className="block text-sm font-medium mb-1">Concerns to Discuss</label><textarea value={formData.concerns_to_discuss} onChange={e => setFormData({...formData, concerns_to_discuss: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm min-h-[60px]" placeholder="Key concerns or symptoms to discuss..." /></div>
                </div>

                <div className="border-t pt-4"><h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2"><Stethoscope className="w-4 h-4" /> Post-Appointment Notes</h3>
                  <div className="mb-3"><label className="block text-sm font-medium mb-1">Summary</label><textarea value={formData.summary} onChange={e => setFormData({...formData, summary: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm min-h-[80px]" placeholder="Summary of what was discussed..." /></div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div><label className="block text-xs font-medium mb-1 text-blue-700">Meds Ordered</label>
                      <div className="flex gap-1"><Input value={medInput.ordered} onChange={e => setMedInput({...medInput, ordered: e.target.value})} className="text-xs" placeholder="Add..." onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addToList('meds_ordered', medInput.ordered, setMedInput, 'ordered'))} /><Button type="button" variant="outline" size="sm" onClick={() => addToList('meds_ordered', medInput.ordered, setMedInput, 'ordered')}>+</Button></div>
                      {formData.meds_ordered?.map((m, i) => <span key={i} className="text-xs block mt-1">{m} <X className="w-3 h-3 inline cursor-pointer text-red-500" onClick={() => removeFromList('meds_ordered', i)} /></span>)}
                    </div>
                    <div><label className="block text-xs font-medium mb-1 text-yellow-700">Meds Changed</label>
                      <div className="flex gap-1"><Input value={medInput.changed} onChange={e => setMedInput({...medInput, changed: e.target.value})} className="text-xs" placeholder="Add..." onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addToList('meds_changed', medInput.changed, setMedInput, 'changed'))} /><Button type="button" variant="outline" size="sm" onClick={() => addToList('meds_changed', medInput.changed, setMedInput, 'changed')}>+</Button></div>
                      {formData.meds_changed?.map((m, i) => <span key={i} className="text-xs block mt-1">{m} <X className="w-3 h-3 inline cursor-pointer text-red-500" onClick={() => removeFromList('meds_changed', i)} /></span>)}
                    </div>
                    <div><label className="block text-xs font-medium mb-1 text-red-700">Meds Discontinued</label>
                      <div className="flex gap-1"><Input value={medInput.discontinued} onChange={e => setMedInput({...medInput, discontinued: e.target.value})} className="text-xs" placeholder="Add..." onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addToList('meds_discontinued', medInput.discontinued, setMedInput, 'discontinued'))} /><Button type="button" variant="outline" size="sm" onClick={() => addToList('meds_discontinued', medInput.discontinued, setMedInput, 'discontinued')}>+</Button></div>
                      {formData.meds_discontinued?.map((m, i) => <span key={i} className="text-xs block mt-1">{m} <X className="w-3 h-3 inline cursor-pointer text-red-500" onClick={() => removeFromList('meds_discontinued', i)} /></span>)}
                    </div>
                  </div>
                  <div className="mb-3"><label className="block text-sm font-medium mb-1 text-purple-700">Labs Ordered</label>
                    <div className="flex gap-2"><Input value={labInput} onChange={e => setLabInput(e.target.value)} placeholder="Add lab..." onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addToList('labs_ordered', labInput, setLabInput))} /><Button type="button" variant="outline" onClick={() => addToList('labs_ordered', labInput, setLabInput)}>Add</Button></div>
                    {formData.labs_ordered?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{formData.labs_ordered.map((l, i) => <span key={i} className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full flex items-center gap-1">{l} <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromList('labs_ordered', i)} /></span>)}</div>}
                  </div>
                  <div className="mb-3"><label className="block text-sm font-medium mb-1">Labs Reviewed</label><textarea value={formData.labs_reviewed} onChange={e => setFormData({...formData, labs_reviewed: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm min-h-[60px]" placeholder="Notes on labs reviewed during visit..." /></div>
                  <div className="mb-3"><label className="block text-sm font-medium mb-1">Follow-up Instructions</label><textarea value={formData.follow_up_instructions} onChange={e => setFormData({...formData, follow_up_instructions: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm min-h-[60px]" placeholder="Instructions from provider..." /></div>
                  <div><label className="block text-sm font-medium mb-1">Additional Notes</label><textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm min-h-[60px]" placeholder="Any other notes..." /></div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-accent hover:bg-accent/90"><Check className="w-4 h-4 mr-2" /> Save</Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Provider Modal */}
      {showProviderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between mb-4">
                <h2 className="text-xl font-bold">{editingProvider ? 'Edit' : 'Add'} Provider</h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowProviderModal(false); resetProviderForm(); }}><X className="w-5 h-5" /></Button>
              </div>
              <form onSubmit={handleProviderSubmit} className="space-y-4">
                <div><label className="block text-sm font-medium mb-1">Name *</label><Input value={providerForm.name} onChange={e => setProviderForm({...providerForm, name: e.target.value})} placeholder="Dr. Jane Smith" required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Title</label><Input value={providerForm.title} onChange={e => setProviderForm({...providerForm, title: e.target.value})} placeholder="MD, PhD" /></div>
                  <div><label className="block text-sm font-medium mb-1">Specialty</label>
                    <select value={providerForm.specialty} onChange={e => setProviderForm({...providerForm, specialty: e.target.value})} className="w-full h-10 px-3 border rounded-lg text-sm">
                      <option value="">Select...</option>{specialties.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div><label className="block text-sm font-medium mb-1">Practice/Company</label><Input value={providerForm.company_name} onChange={e => setProviderForm({...providerForm, company_name: e.target.value})} placeholder="Children's Hospital" /></div>
                <div><label className="block text-sm font-medium mb-1">Address</label><Input value={providerForm.address} onChange={e => setProviderForm({...providerForm, address: e.target.value})} placeholder="123 Medical Center Dr" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Phone</label><Input value={providerForm.phone} onChange={e => setProviderForm({...providerForm, phone: e.target.value})} placeholder="(555) 123-4567" /></div>
                  <div><label className="block text-sm font-medium mb-1">Email</label><Input type="email" value={providerForm.email} onChange={e => setProviderForm({...providerForm, email: e.target.value})} placeholder="office@provider.com" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Fax</label><Input value={providerForm.fax} onChange={e => setProviderForm({...providerForm, fax: e.target.value})} placeholder="(555) 123-4568" /></div>
                  <div><label className="block text-sm font-medium mb-1">Website</label><Input value={providerForm.website} onChange={e => setProviderForm({...providerForm, website: e.target.value})} placeholder="www.provider.com" /></div>
                </div>
                <div><label className="block text-sm font-medium mb-1">Notes</label><textarea value={providerForm.notes} onChange={e => setProviderForm({...providerForm, notes: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm min-h-[60px]" placeholder="Additional notes about this provider..." /></div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setProviderForm({...providerForm, is_favorite: !providerForm.is_favorite})} className={`p-2 rounded-lg ${providerForm.is_favorite ? 'bg-yellow-100' : 'bg-stone-100'}`}>
                    <Star className={`w-5 h-5 ${providerForm.is_favorite ? 'text-yellow-500 fill-yellow-500' : 'text-stone-400'}`} />
                  </button>
                  <span className="text-sm text-[#57534E]">Mark as favorite for quick access</span>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowProviderModal(false); resetProviderForm(); }} className="flex-1">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-accent hover:bg-accent/90"><Check className="w-4 h-4 mr-2" /> Save Provider</Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AppointmentTracker;
